import { useEffect, useState } from "react";
import { Trophy, Ticket, Archive } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { CustomerNotes } from "@/components/CustomerNotes";
import { CustomerTags } from "@/components/CustomerTags";
import { CustomerTimeline } from "@/components/CustomerTimeline";
import { CustomerConsents } from "@/components/CustomerConsents";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const receiptState = (p) => {
  if (p.result !== "win") return null;
  if (p.redeemed) return ["Ritirato", "bg-slate-500/15 text-slate-300 border-slate-500/30"];
  if (p.expired) return ["Scaduta", "bg-red-500/15 text-red-400 border-red-500/30"];
  return ["Da ritirare", "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"];
};

const receiptNote = (p) => {
  if (p.redeemed) return ` · ritirato il ${fmt(p.redeemed_at)}${p.redeemed_store ? ` presso ${p.redeemed_store}` : ""}`;
  return p.expires_at ? ` · scade il ${fmt(p.expires_at)}` : "";
};

function PlayItem({ p }) {
  const st = receiptState(p);
  return (
    <div className="rounded-lg border border-border bg-card p-3 text-sm" data-testid={`customer-play-${p.id}`}>
      <div className="flex items-center gap-2">
        {p.result === "win" ? <Trophy className="h-4 w-4 text-amber-400" /> : <Ticket className="h-4 w-4 text-muted-foreground" />}
        <span className="font-medium flex-1">{p.result === "win" ? p.prize_name : "Nessun premio"}</span>
        <Badge variant="outline" className="border-border text-xs">{p.channel === "whatsapp" ? "WhatsApp" : "Totem"}</Badge>
        {st && <Badge className={`border text-xs ${st[1]}`}>{st[0]}</Badge>}
      </div>
      <p className="text-xs text-muted-foreground mt-1">{p.contest_name} · {fmt(p.created_at)}{p.store_name ? ` · ${p.store_name}` : ""}</p>
      {p.result === "win" && <p className="text-xs mt-1">Seriale <span className="font-mono-code">{p.receipt_serial}</span>{receiptNote(p)}</p>}
    </div>
  );
}

function PlayHistory({ plays }) {
  return (
    <div>
      <h3 className="text-sm font-semibold mb-2">Cronologia giocate</h3>
      {plays.length === 0 && <p className="text-sm text-muted-foreground">Nessuna giocata</p>}
      <div className="space-y-2" data-testid="customer-detail-plays">{plays.map((p) => <PlayItem key={p.id} p={p} />)}</div>
    </div>
  );
}

const SUMMARY = (d, c) => [["Giocate", d.summary.plays], ["Vincite", d.summary.wins], ["Ritirati", d.summary.redeemed], ["Consenso", c.marketing_consent ? "Sì" : "No"]];

export const CustomerDetailSheet = ({ customerId, onClose, onTagsChange }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    if (customerId) api.customerDetail(customerId).then(setD).catch(() => setD({ error: true }));
  }, [customerId]);
  const c = d?.customer;
  return (
    <Sheet open={!!customerId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto bg-background border-border" data-testid="customer-detail-sheet">
        <SheetHeader>
          <SheetTitle data-testid="customer-detail-name">{c ? c.name || c.email : "Caricamento…"}</SheetTitle>
          <SheetDescription>{c ? `${c.email}${c.phone ? ` · ${c.phone}` : ""} · registrato il ${fmt(c.created_at)}` : ""}</SheetDescription>
        </SheetHeader>
        {d?.error && <p className="text-red-400 mt-4">Cliente non trovato</p>}
        {c && (
          <div className="space-y-5 mt-5">
            <div className="grid grid-cols-4 gap-2 text-center" data-testid="customer-detail-summary">
              {SUMMARY(d, c).map(([l, v]) => (
                <div key={l} className="rounded-lg border border-border bg-card p-3"><p className="text-xs text-muted-foreground">{l}</p><p className="font-display font-bold text-lg">{v}</p></div>
              ))}
            </div>
            <CustomerTags customerId={customerId} tags={c.tags || []} onChange={(tags) => { setD({ ...d, customer: { ...c, tags } }); onTagsChange?.(customerId, tags); }} />
            <a href={api.customerGdprZipUrl(customerId)} className="flex items-center gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 hover:border-emerald-400" data-testid="customer-gdpr-package">
              <span className="h-9 w-9 rounded-lg bg-emerald-500/15 grid place-items-center"><Archive className="h-4 w-4 text-emerald-400" /></span>
              <span className="text-sm"><b>Pacchetto dati GDPR (.zip)</b><span className="block text-xs text-muted-foreground">Profilo, giocate, consensi, sconti e comunicazioni in JSON + Excel, pronto da inviare al cliente (art. 15/20 GDPR). L'export viene registrato nel log attività.</span></span>
            </a>
            {d.rules_accepted?.length > 0 && (
              <div data-testid="customer-rules-accepted">
                <h3 className="text-sm font-semibold mb-2">Regolamenti accettati</h3>
                <ul className="text-xs text-muted-foreground space-y-1">{d.rules_accepted.map((r) => <li key={r.contest_id}>✓ {r.contest_name || r.contest_id} · reg. v{r.version}{r.privacy_version ? ` · privacy v${r.privacy_version}` : ""} · {fmt(r.accepted_at)}</li>)}</ul>
              </div>
            )}
            <CustomerConsents customerId={customerId} />
            <CustomerNotes customerId={customerId} />
            <CustomerTimeline customerId={customerId} />
            <PlayHistory plays={d.plays} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
